import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import APIKit from "../../config/APIKit";

export const fetchCategories = createAsyncThunk(
  "categories/fetchCategories",
  async ({page = 1, limit = 20, search = ''}, { rejectWithValue }) => {
    try {
      const response = await APIKit.get(`/category/list`,{
        params: {
          page,
          limit,
          search
        },
      });
      return response.data;
    } catch (error) {
      if (error && typeof error === "object" && error.response) {
        return rejectWithValue(error.response?.data || "Categories get failed");
      }
      return rejectWithValue("Categories get failed");
    }
  }
);

const categoriesSlice = createSlice({
  name: "categories",
  initialState: {
    categories: [],
    status: "idle",
    error: null,
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      itemsPerPage: 20,
      hasMore: false,
    },
    isLoadingMore: false,
  },
  reducers: {
    clearCategories: (state) => {
      state.categories = [];
      state.status = "idle";
      state.error = null;
      state.pagination = {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 20,
        hasMore: false,
      };
      state.isLoadingMore = false;
    },
    setLoadingMore: (state, action) => {
      state.isLoadingMore = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCategories.pending, (state, action) => {
        const page = action.meta.arg.page || 1;
        if (page === 1) {
          state.status = "loading";
        } else {
          state.isLoadingMore = true;
        }
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.isLoadingMore = false;
        
        const page = action.meta.arg.page || 1;
        const limit = action.meta.arg.limit || 20;
        let newCategories = [];
        let totalCount = 0;
        if (action.payload.data && Array.isArray(action.payload.data.list)) {
          // API structure: { status, message, data: { list: [], totalCount: N } }
          newCategories = action.payload.data.list;
          totalCount = action.payload.data.totalCount || newCategories.length;
        } else if (action.payload.list && Array.isArray(action.payload.list)) {
          // Direct structure: { list: [], totalCount: N }
          newCategories = action.payload.list;
          totalCount = action.payload.totalCount || newCategories.length;
        } else if (Array.isArray(action.payload)) {
          newCategories = action.payload;
          totalCount = action.payload.length;
        } else if (action.payload.data && Array.isArray(action.payload.data)) {
          newCategories = action.payload.data;
          totalCount = action.payload.data.length;
          
        } else {
          newCategories = action.payload;
          totalCount = action.payload.length;
        }
        
        // Calculate pagination metadata from totalCount
        const totalPages = Math.ceil(totalCount / limit);
        const currentLoadedCount = page === 1 ? newCategories.length : state.categories.length + newCategories.length;
        
        state.pagination = {
          currentPage: page,
          totalPages: totalPages,
          totalItems: totalCount,
          itemsPerPage: limit,
          hasMore: currentLoadedCount < totalCount,
        };
        // Append categories if it's a pagination request (page > 1), otherwise replace
        if (page === 1) {
          state.categories = newCategories;
        } else {
          state.categories = [...state.categories, ...newCategories];
        }
        
        state.error = null;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.status = "failed";
        state.isLoadingMore = false;
        state.error =
          typeof action.payload === "string"
            ? action.payload
            : "categories get failed";
      });
  },
});

export const { clearCategories, setLoadingMore } = categoriesSlice.actions;
export default categoriesSlice.reducer;
